<template>
  <div id="base" class="h-full">
    <Navbar
      v-if="
        route.meta.includeNavbar === 'isAuthed'
          ? authStore.isAuthed
          : (route.meta.includeNavbar ?? true)
      "
      :class="{ 'mb-4': !checkRoute }"
    />
    <PopupMessageOverlay
      v-if="authStore.isAuthed || showRefreshPopup"
      :show-refresh-popup="showRefreshPopup"
      @refresh-page="onRefreshPage"
    />
    <div
      id="main-base"
      class="min-h-screen w-full"
      :class="{
        'px-2': route.name !== 'register-user',
        'xl:container': route.meta.containered ?? true,
      }"
    >
      <RouterView />

      <NewFeaturePopup
        v-if="newFeaturePopupState.show"
        :feature-key="newFeaturePopupState.key"
      />
    </div>
    <!-- Global Slideover Panels -->
    <AIChatSlideoverPanel v-if="authStore.isAuthed" />
    <!-- My Tasks covers BOTH classical recommendation tasks (CoPilot,
         AutoPilot etc.) and AI suggestions, so it must mount whenever a
         user is signed in — not just on AI-enabled orgs. The backend
         already returns the classical assignments regardless of the AI
         flag; gating this on `aiRecommendationsEnabled` hid the panel
         from every org without AI, leaving them no way to see the
         tasks already assigned to them. The AI-specific rows inside
         the panel are conditionally rendered based on the same flag
         server-side. -->
    <MyTasksSlideoverPanel v-if="authStore.isAuthed" />

    <!-- Right-edge toggle buttons (stacked, shift as a unit) -->
    <div
      v-if="
        authStore.isAuthed &&
        !aiChat.isResizing.value &&
        !myTasks.isResizing.value &&
        showGlobalButtonStack
      "
      class="fixed z-40 flex flex-col transition-[right] duration-500 ease-in-out"
      :style="{
        top: '16rem',
        right:
          aiChat.aiChatVisible.value && aiChat.isOpen.value
            ? `${aiChat.panelWidth.value}px`
            : myTasks.isOpen.value
              ? `${myTasks.panelWidth.value}px`
              : '0px',
      }"
    >
      <!-- AI Chat button (top) -->
      <button
        v-if="showAiChatButton"
        v-tooltip="'Automagic AI'"
        type="button"
        aria-label="Automagic AI"
        :aria-pressed="aiChat.isOpen.value"
        class="border border-gray-200 px-3 py-2 transition-colors"
        :class="
          aiChat.isOpen.value
            ? 'rounded-tl-md border-r-0 bg-purple-500 shadow-md'
            : 'rounded-tl-md border-r bg-white shadow hover:bg-gray-50'
        "
        @click="handleToggleAiChat()"
        @mouseenter="aiButtonHover = true"
        @mouseleave="aiButtonHover = false"
      >
        <FyrAiLogo
          :size="24"
          :hover="aiButtonHover"
          :class="
            aiChat.isOpen.value
              ? 'text-white'
              : aiChat.aiChatConsentPending.value
                ? 'text-gray-400'
                : 'text-gray-600'
          "
        />
      </button>

      <!-- My Tasks button (bottom) -->
      <button
        v-if="showMyTasksButton"
        v-tooltip="'My Tasks'"
        type="button"
        aria-label="My Tasks"
        :aria-pressed="myTasks.isOpen.value"
        class="border border-gray-200 px-3 py-2 transition-colors"
        :class="[
          myTasks.isOpen.value
            ? 'border-r-0 bg-purple-500 shadow-md'
            : 'border-r bg-white shadow hover:bg-gray-50',
          showAiChatButton ? 'rounded-bl-md border-t-0' : 'rounded-l-md',
        ]"
        @click="handleToggleMyTasks()"
      >
        <Icon
          name="check-circle"
          class="size-6"
          :class="myTasks.isOpen.value ? 'text-white' : 'text-gray-600'"
        />
      </button>
    </div>
    <div id="table-preview-teleport"></div>
    <Footer v-if="route.meta.includeFooter ?? true" />
  </div>
  <VueQueryDevtools />
</template>

<script setup lang="ts">
import { useLogoutInterceptor } from "@/services/api";
import {
  useRequiresAuthNavigationGuard,
  useHomeRouteRedirectGuard,
} from "@/router";
import { computed, provide, reactive, ref, watch } from "vue";
import Navbar from "@/components/Navigation/Navbar.vue";
import Footer from "@/components/Core/Footer.vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth";
import PopupMessageOverlay from "@/components/Core/PopupMessages/PopupMessageOverlay.vue";
import { useNewVersionCheck } from "@/services/version";
import { useSetDefaultTracking, useTrackLogin } from "@/services/gtm";
import {
  watchRouteForAccessTier,
  watchRouteForFeatureFlag,
  watchRouteForOrganization,
  watchRouteForUserRole,
  watchRouteForPermissionRequirement,
  watchRouteForResetFilters,
} from "@/router";
import { useQueryClient } from "@tanstack/vue-query";
import { useHead } from "@unhead/vue";
import { useUpdateTemporalFilter } from "./store/data_filters";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import NewFeaturePopup from "@/components/Core/PopupMessages/NewFeaturePopup.vue";
import { NEW_FEATURE_POP_UP } from "@/utils/newFeature";
import FyrAiLogo from "@/components/Core/FyrAiLogo.vue";
import Icon from "@/components/Core/Icon.vue";
import AIChatSlideoverPanel from "@/components/AIAgent/AIChatSlideoverPanel.vue";
import MyTasksSlideoverPanel from "@/components/Core/Recommendations/MyTasksSlideoverPanel.vue";
import {
  useAIChatSlideover,
  resetAIChatState,
} from "@/composables/useAIChatSlideover";
import {
  useMyTasksSlideover,
  resetMyTasksState,
} from "@/composables/useMyTasksSlideover";
import { onSelectedOrganizationChange } from "@/services/user";

const router = useRouter();
const authStore = useAuthStore();
const route = useRoute();
const aiChat = useAIChatSlideover();
const myTasks = useMyTasksSlideover();
const showAiChatButton = computed(
  () => aiChat.aiChatVisible.value && !aiChat.hasLocalButton.value,
);
// My Tasks button is available to every authenticated user. It covers
// classical recommendation assignments (CoPilot / AutoPilot tasks)
// alongside AI suggestions, so we no longer gate it on the AI flag —
// the panel renders an empty state when the user has nothing assigned,
// which is harmless. `hasLocalButton` is still honored so views that
// embed their own trigger button (e.g. the recommendations overview)
// don't get a duplicate global one.
const showMyTasksButton = computed(() => !myTasks.hasLocalButton.value);
const showGlobalButtonStack = computed(
  () => showAiChatButton.value || showMyTasksButton.value,
);

// If the AI chat feature gate flips off at runtime (feature-flag
// refetch on org switch, consent revocation), close the chat panel so
// its `panelWidth` can't keep contributing to the right-edge button
// stack offset. Without this, the button stack renders shifted by the
// panel's width even though the panel itself is `v-if`'d out.
// My Tasks doesn't need a parallel watcher: it's no longer feature-
// gated, so there's no flip-off event to react to.
watch(
  () => aiChat.aiChatVisible.value,
  (visible) => {
    if (!visible && aiChat.isOpen.value) {
      aiChat.close();
    }
  },
);

function handleToggleAiChat(): void {
  if (!aiChat.isOpen.value) {
    myTasks.close();
  }
  aiChat.toggle();
}

function handleToggleMyTasks(): void {
  if (!myTasks.isOpen.value) {
    aiChat.close();
  }
  myTasks.toggle();
}

const queryClient = useQueryClient();
const showRefreshPopup = useNewVersionCheck();
const aiButtonHover = ref(false);
const checkRoute = computed(() => {
  return (
    route.name === "dashboards" ||
    route.name === "edit-dashboard" ||
    route.name === "report" ||
    route.name === "view-dashboard" ||
    route.name === "shared-dashboard" ||
    route.name === "report-editor"
  );
});
const newFeaturePopupState = reactive({
  key: "",
  show: false,
});
provide("newFeaturePopupState", newFeaturePopupState);
watch(
  () => route.name,
  (newName) => {
    const routeName = newName as string;
    if (routeName && NEW_FEATURE_POP_UP[routeName]) {
      newFeaturePopupState.key = routeName;
      newFeaturePopupState.show = true;
    } else {
      newFeaturePopupState.show = false;
    }
  },
);

function onRefreshPage(): void {
  location.reload();
}

watch(
  () => authStore.isAuthed,
  (isAuthed) => {
    if (!isAuthed) {
      router.replace({
        name: "login",
        query: { redirect: router.currentRoute.value.fullPath },
      });
      queryClient.clear();
      resetAIChatState();
      resetMyTasksState();
    }
  },
);

onSelectedOrganizationChange(() => {
  resetAIChatState();
  resetMyTasksState();
});

watchRouteForAccessTier();
watchRouteForUserRole();
watchRouteForOrganization();
watchRouteForPermissionRequirement();
watchRouteForFeatureFlag();
watchRouteForResetFilters();

useLogoutInterceptor();
useRequiresAuthNavigationGuard();
useHomeRouteRedirectGuard();
useSetDefaultTracking();
useTrackLogin();

useHead({
  titleTemplate: (title?: string) => (title ? `${title} | Fyr` : "Fyr"),
  title: computed(() => route.meta.title),
});

useUpdateTemporalFilter();
</script>
